import { WeightedSkill } from "@/modules/recruitment/entities/WeightedSkill"

export const LINKEDIN_FEED_CHAR_LIMIT = 3000

const SECTION_SEPARATOR = "\n\n"
const ELLIPSIS = "…"
const DESC_HEADER = "ABOUT THE ROLE\n"

export interface LinkedInShareVacancyInput {
  id: string
  title: string
  description?: string | null
  department?: string
  locationType?: string
  jobType?: string
  organizationName?: string | null
  minimumSalary?: number
  maximumSalary?: number
  yearsOfExperience?: number
  educationLevel?: string | null
  keyResponsibilities?: WeightedSkill[]
  requiredSkills?: WeightedSkill[]
  optionalSkills?: WeightedSkill[]
  certifications?: (WeightedSkill | string)[]
}

export interface BuildLinkedInShareTextParams {
  vacancy: LinkedInShareVacancyInput
  organizationId: string
  organizationName?: string | null
  applyLinkBase: string
}

export type DroppedSection =
  | "description"
  | "responsibilities"
  | "requiredSkills"
  | "qualifications"
  | "niceToHave"

export interface BuildLinkedInShareTextResult {
  text: string
  raw: string
  truncated: boolean
  droppedSections: DroppedSection[]
  applyLink: string
}

function formatBulletList(items: string[]): string {
  return items.map((item) => `- ${item}`).join("\n")
}

function formatCurrencyRange(min?: number, max?: number): string {
  if ((min ?? 0) > 0 && (max ?? 0) > 0) {
    const formatter = new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    })
    return `${formatter.format(min as number)} - ${formatter.format(max as number)}`
  }
  return "Competitive"
}

export function buildApplyToken(vacancyId: string, organizationId: string): string {
  return btoa(`${vacancyId}|${organizationId}`)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "")
}

export function buildLinkedInApplyLink(
  vacancyId: string,
  organizationId: string,
  applyLinkBase: string,
): string {
  return applyLinkBase + buildApplyToken(vacancyId, organizationId)
}

export function buildLinkedInShareText({
  vacancy,
  organizationId,
  organizationName,
  applyLinkBase,
}: BuildLinkedInShareTextParams): BuildLinkedInShareTextResult {
  const applyLink = buildLinkedInApplyLink(vacancy.id, organizationId, applyLinkBase)

  const orgName = organizationName?.trim() || vacancy.organizationName?.trim()
  const intro = `${orgName ? `${orgName} is` : "We are"} looking for a ${vacancy.title} to join our team.`

  const metaLines = [
    vacancy.locationType ? `Location: ${vacancy.locationType}` : "",
    vacancy.jobType
      ? `Employment Type: ${String(vacancy.jobType).replace(/([a-z])([A-Z])/g, "$1 $2")}`
      : "",
    `Salary: ${formatCurrencyRange(vacancy.minimumSalary, vacancy.maximumSalary)}`,
  ].filter(Boolean)
  const meta = metaLines.join("\n")

  const applyBlock = `HOW TO APPLY\nApply here: ${applyLink}`
  const closing = `Join us in building the future. We offer competitive compensation, flexible working, and opportunities for growth.`
  const hashtags = `#Hiring #JobOpportunity #Careers`

  const qualifications: string[] = []
  if ((vacancy.yearsOfExperience ?? 0) > 0) {
    qualifications.push(`${vacancy.yearsOfExperience}+ years of experience`)
  }
  if (vacancy.educationLevel) qualifications.push(vacancy.educationLevel)
  if (vacancy.certifications?.length) {
    qualifications.push(
      ...vacancy.certifications.map((c) => (typeof c === "string" ? c : c.name)),
    )
  }

  const descBlock = vacancy.description ? `${DESC_HEADER}${vacancy.description}` : null
  const respBlock = vacancy.keyResponsibilities?.length
    ? `KEY RESPONSIBILITIES\n${formatBulletList(vacancy.keyResponsibilities.map((kr) => kr.name))}`
    : null
  const skillsBlock = vacancy.requiredSkills?.length
    ? `REQUIRED SKILLS\n${formatBulletList(vacancy.requiredSkills.map((rs) => rs.name))}`
    : null
  const qualBlock = qualifications.length
    ? `QUALIFICATIONS\n${formatBulletList(qualifications)}`
    : null
  const niceBlock = vacancy.optionalSkills?.length
    ? `NICE TO HAVE\n${formatBulletList(vacancy.optionalSkills.map((os) => os.name))}`
    : null

  // Positions 0..6; description at index 2 so it can be truncated in place.
  const displayParts: (string | null)[] = [
    intro,
    meta || null,
    descBlock,
    respBlock,
    skillsBlock,
    qualBlock,
    niceBlock,
    applyBlock,
    closing,
    hashtags,
  ]

  const assemble = () =>
    displayParts.filter((p): p is string => Boolean(p)).join(SECTION_SEPARATOR)

  const raw = assemble()

  let text = raw
  if (text.length <= LINKEDIN_FEED_CHAR_LIMIT) {
    return { text, raw, truncated: false, droppedSections: [], applyLink }
  }

  const dropped: DroppedSection[] = []

  // Drop the least-valuable optional sections first, in this order.
  const dropOrder: { key: DroppedSection; index: number }[] = [
    { key: "niceToHave", index: 6 },
    { key: "qualifications", index: 5 },
    { key: "requiredSkills", index: 4 },
    { key: "responsibilities", index: 3 },
  ]
  for (const { key, index } of dropOrder) {
    if (!displayParts[index]) continue
    displayParts[index] = null
    dropped.push(key)
    text = assemble()
    if (text.length <= LINKEDIN_FEED_CHAR_LIMIT) {
      return { text, raw, truncated: true, droppedSections: dropped, applyLink }
    }
  }

  // Still over budget — truncate the description with an ellipsis.
  if (vacancy.description && displayParts[2]) {
    const overshoot = text.length - LINKEDIN_FEED_CHAR_LIMIT + ELLIPSIS.length
    const newDescLen = vacancy.description.length - overshoot
    if (newDescLen <= 0) {
      displayParts[2] = null
      dropped.push("description")
    } else {
      let sliced = vacancy.description.slice(0, newDescLen).trimEnd()
      // Back up to the previous word boundary — but only if it's within 20%
      // of the target, so a description with no nearby spaces isn't gutted.
      const lastSpace = sliced.lastIndexOf(" ")
      if (lastSpace > 0 && lastSpace >= newDescLen * 0.8) {
        sliced = sliced.slice(0, lastSpace).trimEnd()
      }
      displayParts[2] = `${DESC_HEADER}${sliced}${ELLIPSIS}`
    }
    text = assemble()
  }

  // Ultimate safety net: hard slice if somehow still over (e.g. huge title/meta).
  if (text.length > LINKEDIN_FEED_CHAR_LIMIT) {
    text = text.slice(0, LINKEDIN_FEED_CHAR_LIMIT)
  }

  return { text, raw, truncated: true, droppedSections: dropped, applyLink }
}
